import { all, call, put, takeLatest, takeEvery, delay } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    fetchCompaniesFailure,
    fetchCompaniesRequest,
    fetchCompaniesSuccess,
    fetchSectorsFailure,
    fetchSectorsRequest,
    fetchSectorsSuccess,
    fetchWaterAdministrationsFailure,
    fetchWaterAdministrationsRequest,
    fetchWaterAdministrationsSuccess,
    fetchCompanyDetailRequest,
    fetchCompanyDetailSuccess,
    fetchCompanyDetailFailure,
    updateCompanyRequest,
    updateCompanySuccess,
    updateCompanyFailure,
    fetchAllCompaniesSuccess,
    fetchAllCompaniesFailure,
    fetchAllCompaniesRequest,
    fetchCompanyTypesSuccess,
    fetchCompanyTypesFailure,
    fetchCompanyTypesRequest,
    fetchCitiesSuccess,
    fetchCitiesFailure,
    fetchDistrictsSuccess,
    fetchDistrictsFailure,
    fetchCountriesSuccess,
    fetchCountriesFailure,
    fetchCountriesRequest,
    fetchDistrictsRequest,
    fetchCitiesRequest,
    createCompanySuccess,
    createCompanyFailure,
    createCompanyRequest,
    fetchProductFamiliesSuccess,
    fetchProductFamiliesFailure,
    fetchProductFamiliesRequest,
    fetchUserRolesRequest,
    fetchUserRolesSuccess,
    fetchUserRolesFailure,
    createUserRequest,
    createUserSuccess,
    createUserFailure,
    fetchUsersRequest,
    fetchUsersSuccess,
    fetchUsersFailure,
    fetchUserDetailRequest,
    fetchUserDetailSuccess,
    fetchUserDetailFailure,
    updateUserRequest,
    updateUserSuccess,
    updateUserFailure,
    fetchMainSectorsSuccess,
    fetchMainSectorsFailure,
    fetchMainSectorsRequest,
    createGatewayFailure,
    createGatewaySuccess,
    deleteGatewayFailure,
    deleteGatewaySuccess,
    fetchGatewaysFailure,
    fetchGatewaysSuccess,
    updateGatewayFailure,
    updateGatewaySuccess,
    createGatewayRequest,
    deleteGatewayRequest,
    fetchGatewaysRequest,
    updateGatewayRequest,
    createSensorFailure,
    createSensorSuccess,
    fetchSensorsFailure,
    fetchSensorsSuccess,
    deleteSensorFailure,
    deleteSensorSuccess,
    updateSensorFailure,
    updateSensorSuccess,
    createSensorRequest,
    deleteSensorRequest,
    fetchSensorsRequest,
    updateSensorRequest,
    fetchBasinsFailure,
    fetchBasinsRequest,
    fetchBasinsSuccess,
    fetchSubscriptionTypesRequest,
    fetchSubscriptionTypesSuccess,
    fetchSubscriptionTypesFailure,
    fetchWaterAdministrationMainSectorsRequest,
    fetchWaterAdministrationMainSectorsSuccess,
    fetchWaterAdministrationMainSectorsFailure,
    fetchWaterAdministrationSubSectorsRequest,
    fetchWaterAdministrationSubSectorsSuccess,
    fetchWaterAdministrationSubSectorsFailure,
    clearWaterAdministrationSubSectors,
    resetWaterAdministrationSectorState,
    fetchDatasetListStart,
    fetchDatasetListSuccess,
    fetchDatasetListFailure,
    startCopernicusQuery,
    copernicusQuerySuccess,
    copernicusQueryFailure,
    clearQueryStatus

} from '../reducers/clients/clientsSlice';
import { CompanyListData, Sector, WaterAdministration, SubscriptionType, CompanyDetail, CompanyUpdateData, Company, CompanyType, City, District, Country, CompanyCreateData, ProductFamilyTree, CreatedUser, User, UserCreateData, UserRole, UserDetail, UserUpdateData, MainSector, Gateway, GatewayCreateData, GatewayUpdateData, Sensor, SensorCreateData, SensorUpdateData, Basin, WaterAdministrationMainSector, WaterAdministrationSubSector, CopernicusQueryPayload, ClimateBatchResult, ClimateDatasetListItem, GldasQueryPayload, GraceQueryPayload, SatelliteQueryResult, SatelliteSyncStatusResponse } from '../reducers/clients/clients.types';
import {
    getCompanyList, getSectorData, getWaterAdministrationData, getCompanyById, updateCompany, getCompanyTypes, getCities, getDistricts, getCountries, createCompany, getProductFamilyTree, getUserRoles,
    createUser,
    getAllUsers,
    getUserById,
    updateUser,
    getMainSectorData,
    createGateway,
    deleteGateway,
    updateGateway,
    getGatewaysByCompanyId,
    createSensor,
    getSensorsByCompanyId,
    deleteSensor,
    updateSensor,
    getBasins,
    getSubscriptionData,
    getWaterAdministrationMainSectorData,
    getWaterAdministrationSubSectorData,
    executeCopernicusQuery,
    executeGldasQuery,
    executeGraceQuery,
    triggerAllSatelliteQueries,
    checkSatelliteSyncStatus
} from '../../services/clients';

import { extractError } from "../../utils/helpers/api.helper";

function* fetchMainSectorsSaga() {
    try {
        const response: { data: { mainSectors: MainSector[] } } = yield call(getMainSectorData);
        yield put(fetchMainSectorsSuccess(response.data.mainSectors));
    } catch (error: any) {
        yield put(fetchMainSectorsFailure(error.response.errorCode || 'An error occurred'));
    }
}
function* fetchSectorsSaga() {
    try {
        const response: { data: { sectors: Sector[] } } = yield call(getSectorData);
        yield put(fetchSectorsSuccess(response.data.sectors));
    } catch (error: any) {
        yield put(fetchSectorsFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchWaterAdministrationsSaga() {
    try {
        const response: { data: { waterAdministrations: WaterAdministration[] } } = yield call(getWaterAdministrationData);
        yield put(fetchWaterAdministrationsSuccess(response.data.waterAdministrations));
    } catch (error: any) {
        yield put(fetchWaterAdministrationsFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchSubscriptionTypesSaga(action: PayloadAction<number>) {
    try {
        const response: {
            data: {
                waterAdministration?: WaterAdministration & { subscriptionTypes?: SubscriptionType[] };
            }
        } = yield call(getSubscriptionData, action.payload);

        const subscriptions =
            response.data.waterAdministration?.subscriptionTypes ?? [];

        yield put(fetchSubscriptionTypesSuccess(subscriptions));
    } catch (error: unknown) {
        const apiError = error as {
            response?: { errorCode?: unknown; data?: { errorKey?: unknown; errorCode?: unknown } };
        };

        const errorCode =
            (typeof apiError.response?.errorCode === 'string' && apiError.response.errorCode) ||
            (typeof apiError.response?.data?.errorCode === 'string' && apiError.response.data.errorCode) ||
            (typeof apiError.response?.data?.errorKey === 'string' && apiError.response.data.errorKey) ||
            'An error occurred';

        yield put(fetchSubscriptionTypesFailure(errorCode));
    }
}


function* fetchCompaniesSaga() {
    try {
        const response: { data: { companies: CompanyListData[] } } = yield call(getCompanyList);
        yield put(fetchCompaniesSuccess(response.data.companies));
    } catch (error: any) {
        yield put(fetchCompaniesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchCompanyDetailSaga(action: PayloadAction<string>) {
    try {
        const companyId = action.payload;
        const response: { data: { company: CompanyDetail } } = yield call(getCompanyById, companyId);
        yield put(fetchCompanyDetailSuccess(response.data.company));
    } catch (error: any) {
        yield put(fetchCompanyDetailFailure(error.response.errorCode || 'Company detail fetch failed'));
    }
}

function* updateCompanySaga(action: PayloadAction<{ id: string; data: CompanyUpdateData }>) {
    try {
        const { id, data } = action.payload;
        const response: { data: { company: CompanyDetail } } = yield call(updateCompany, id, data);
        yield put(updateCompanySuccess(response.data.company));
    } catch (error: any) {
        yield put(updateCompanyFailure(error.response?.data?.errorKey || 'Company update failed'));
    }
}

function* fetchAllCompaniesSaga() {
    try {
        const response: { data: { companies: Company[] } } = yield call(getCompanyList);
        yield put(fetchAllCompaniesSuccess(response.data.companies));
    } catch (error: any) {
        yield put(fetchAllCompaniesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchCompanyTypesSaga() {
    try {
        const response: { data: { companyTypes: CompanyType[] } } = yield call(getCompanyTypes);
        yield put(fetchCompanyTypesSuccess(response.data.companyTypes));
    } catch (error: any) {
        yield put(fetchCompanyTypesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchCountriesSaga() {
    try {
        const response: { data: { countries: Country[] } } = yield call(getCountries);
        yield put(fetchCountriesSuccess(response.data.countries));
    } catch (error: any) {
        yield put(fetchCountriesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchCitiesSaga(action: PayloadAction<number>) {
    try {
        const countryId = action.payload;
        const response: { data: { cities: City[] } } = yield call(getCities, countryId);
        yield put(fetchCitiesSuccess(response.data.cities));
    } catch (error: any) {
        yield put(fetchCitiesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchDistrictsSaga(action: PayloadAction<number>) {
    try {
        const cityId = action.payload;
        const response: { data: { districts: District[] } } = yield call(getDistricts, cityId);
        yield put(fetchDistrictsSuccess(response.data.districts));
    } catch (error: any) {
        yield put(fetchDistrictsFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* createCompanySaga(action: PayloadAction<CompanyCreateData>) {
    try {
        const companyData = action.payload;
        const response: { data: { company: CompanyDetail } } = yield call(createCompany, companyData);
        yield put(createCompanySuccess(response.data.company));

        const newCompanyId = response.data.company.id;
        const lat = Number(companyData.latitude);
        const lon = Number(companyData.longitude);

        yield call(triggerAllSatelliteQueries, {
            companyId: newCompanyId,
            latitude: lat,
            longitude: lon
        });

        let isPolling = true;
        let attemptCount = 0;
        const maxAttempts = 60;

        while (isPolling && attemptCount < maxAttempts) {
            yield delay(30000);
            attemptCount++;

            const statusResponse: { data: SatelliteSyncStatusResponse } = yield call(checkSatelliteSyncStatus, newCompanyId);

            if (statusResponse.data.isCopernicusReady) {
                yield put(copernicusQuerySuccess({ successful: [], failed: [] }));
            }

            if (statusResponse.data.isAllCompleted) {
                isPolling = false;
            } else if (statusResponse.data.hasError) {
                isPolling = false;
                yield put(copernicusQueryFailure('Sync error occurred during background processing'));
            }
        }

    } catch (error: any) {
        yield put(createCompanyFailure(extractError(error as Error, 'errors:CLIMATE_DATASET_CREATE_FAILED')));
    }
}

function* handleCopernicusQuery(action: PayloadAction<CopernicusQueryPayload>) {
    try {
        const response: { data: { success: boolean; data: ClimateBatchResult } } = yield call(executeCopernicusQuery, action.payload);

        yield put(copernicusQuerySuccess(response.data.data));
        yield put(fetchDatasetListStart());
    } catch (error: any) {
        yield put(copernicusQueryFailure(extractError(error as Error, 'errors:CLIMATE_DATASET_FETCH_FAILED')));
    }
}


function* fetchProductFamiliesSaga() {
    try {
        const response: { data: { productFamilies: ProductFamilyTree[] } } = yield call(getProductFamilyTree);
        yield put(fetchProductFamiliesSuccess(response.data.productFamilies));
    } catch (error: any) {
        yield put(fetchProductFamiliesFailure(error.response.errorCode || 'An error occurred'));
    }
}

function* fetchUserRolesSaga() {
    try {
        const response: { data: { userRoles: UserRole[] } } = yield call(getUserRoles);
        yield put(fetchUserRolesSuccess(response.data.userRoles));
    } catch (error: any) {
        yield put(fetchUserRolesFailure(error.response?.data?.message || 'Failed to fetch user roles'));
    }
}

function* createUserSaga(action: PayloadAction<{ companyId: string; data: UserCreateData }>) {
    try {
        const { companyId, data } = action.payload;
        const response: { data: CreatedUser } = yield call(createUser, companyId, data);
        const createdUserData = { ...response.data, companyId: companyId };
        yield put(createUserSuccess(createdUserData));
    } catch (error: any) {
        yield put(createUserFailure(error.response?.data?.message || 'User creation failed'));
    }
}

function* fetchAllUsersSaga(action: PayloadAction<string>) {
    const companyId = action.payload;
    try {
        const response: { data: { users: User[] } } = yield call(getAllUsers, companyId);
        yield put(fetchUsersSuccess(response.data.users));
    } catch (error: any) {
        yield put(fetchUsersFailure(error.response?.data?.message || 'Failed to fetch users'));
    }
}
function* fetchUserDetailSaga(action: PayloadAction<string>) {
    try {
        const userId = action.payload;
        const response: { data: { user: UserDetail } } = yield call(getUserById, userId);
        yield put(fetchUserDetailSuccess(response.data.user));
    } catch (error: any) {
        yield put(fetchUserDetailFailure(error.response?.data?.message || 'Failed to fetch user details'));
    }
}

function* updateUserSaga(action: PayloadAction<{ userId: string; data: UserUpdateData }>) {
    try {
        const { userId, data } = action.payload;
        const response: { data: { user: UserDetail } } = yield call(updateUser, userId, data);
        yield put(updateUserSuccess(response.data.user));
    } catch (error: any) {
        yield put(updateUserFailure(error.response?.data?.message || 'User update failed'));
    }
}

// --- GATEWAY SAGA WORKERS (WITH RE-FETCH LOGIC) ---

function* fetchGatewaysSaga(action: PayloadAction<string>) {
    try {
        const companyId = action.payload;
        const response: { data: Gateway[] } = yield call(getGatewaysByCompanyId, companyId);
        yield put(fetchGatewaysSuccess(response.data));
    } catch (error: any) {
        yield put(fetchGatewaysFailure(error.response?.data?.errorKey || 'An error occurred'));
    }
}

function* createGatewaySaga(action: PayloadAction<{ companyId: string; data: GatewayCreateData }>) {
    try {
        const { companyId, data } = action.payload;
        const response: { data: { gateway: Gateway } } = yield call(createGateway, companyId, data);
        yield put(createGatewaySuccess(response.data.gateway));
        // --- ADDITION: RE-FETCH GATEWAYS ---
        yield put(fetchGatewaysRequest(companyId));
    } catch (error: any) {
        yield put(createGatewayFailure(error.response?.data?.errorKey || 'Gateway creation failed'));
    }
}

function* updateGatewaySaga(action: PayloadAction<{ gatewayId: string; data: GatewayUpdateData; clientId: string }>) {
    try {
        const { gatewayId, data, clientId } = action.payload; // <-- get clientId from payload
        const response: { data: { gateway: Gateway } } = yield call(updateGateway, gatewayId, data);
        yield put(updateGatewaySuccess(response.data.gateway));
        // --- ADDITION: RE-FETCH GATEWAYS ---
        yield put(fetchGatewaysRequest(clientId));
    } catch (error: any) {
        yield put(updateGatewayFailure(error.response?.data?.errorKey || 'Gateway update failed'));
    }
}

function* deleteGatewaySaga(action: PayloadAction<{ gatewayId: string; clientId: string }>) {
    try {
        const { gatewayId, clientId } = action.payload; // <-- get clientId from payload
        yield call(deleteGateway, gatewayId);
        yield put(deleteGatewaySuccess(gatewayId));
        // --- ADDITION: RE-FETCH GATEWAYS ---
        yield put(fetchGatewaysRequest(clientId));
    } catch (error: any) {
        yield put(deleteGatewayFailure(error.response?.data?.errorKey || 'Gateway deletion failed'));
    }
}


// --- SENSOR SAGA WORKERS (WITH RE-FETCH LOGIC) ---

function* fetchSensorsSaga(action: PayloadAction<string>) {
    try {
        const companyId = action.payload;
        const response: { data: Sensor[] } = yield call(getSensorsByCompanyId, companyId);
        yield put(fetchSensorsSuccess(response.data));
    } catch (error: any) {
        yield put(fetchSensorsFailure(error.response?.data?.errorKey || 'Sensor fetch failed'));
    }
}

function* createSensorSaga(action: PayloadAction<SensorCreateData>) {
    try {
        const sensorData = action.payload;
        const response: { data: { sensor: Sensor } } = yield call(createSensor, sensorData);
        yield put(createSensorSuccess(response.data.sensor));
        // --- ADDITION: RE-FETCH SENSORS ---
        yield put(fetchSensorsRequest(sensorData.companyId));
    } catch (error: any) {
        yield put(createSensorFailure(error.response?.data?.errorKey || 'Sensor creation failed'));
    }
}

function* updateSensorSaga(action: PayloadAction<{ sensorId: string; data: SensorUpdateData; companyId: string }>) {
    try {
        const { sensorId, data, companyId } = action.payload; // <-- companyId is needed for re-fetch
        const response: { data: { sensor: Sensor } } = yield call(updateSensor, sensorId, data);
        yield put(updateSensorSuccess(response.data.sensor));
        // --- ADDITION: RE-FETCH SENSORS ---
        yield put(fetchSensorsRequest(companyId));
    } catch (error: any) {
        yield put(updateSensorFailure(error.response?.data?.errorKey || 'Sensor update failed'));
    }
}

function* deleteSensorSaga(action: PayloadAction<{ sensorId: string; companyId: string }>) {
    try {
        const { sensorId, companyId } = action.payload; // <-- companyId is needed for re-fetch
        yield call(deleteSensor, sensorId);
        yield put(deleteSensorSuccess(sensorId));
        // --- ADDITION: RE-FETCH SENSORS ---
        yield put(fetchSensorsRequest(companyId));
    } catch (error: any) {
        yield put(deleteSensorFailure(error.response?.data?.errorKey || 'Sensor deletion failed'));
    }
}
function* fetchBasinsSaga() {
    try {
        const response: { data: { basins: Basin[] } } = yield call(getBasins);
        // Assuming backend returns { data: { basins: [...] } }
        // If backend returns array directly, adjust accordingly.
        yield put(fetchBasinsSuccess(response.data.basins));
    } catch (error: any) {
        yield put(fetchBasinsFailure(error.response?.data?.errorKey || 'Failed to fetch basins'));
    }
}

function* fetchWaterAdministrationMainSectorsSaga(action: PayloadAction<number>) {
    try {
        const response: { data: WaterAdministrationMainSector[] } = yield call(
            getWaterAdministrationMainSectorData,
            action.payload
        );
        yield put(fetchWaterAdministrationMainSectorsSuccess(response.data));
    } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Ana sektörler getirilemedi.';
        yield put(fetchWaterAdministrationMainSectorsFailure(errorMessage));
    }
}

function* fetchWaterAdministrationSubSectorsSaga(action: PayloadAction<number>) {
    try {
        const response: { data: WaterAdministrationSubSector[] } = yield call(
            getWaterAdministrationSubSectorData,
            action.payload
        );

        yield put(fetchWaterAdministrationSubSectorsSuccess(response.data));
    } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Alt sektörler getirilemedi.';
        yield put(fetchWaterAdministrationSubSectorsFailure(errorMessage));
    }
}

export function* watchClientsSaga() {
    yield all([
        takeLatest(fetchMainSectorsRequest.type, fetchMainSectorsSaga),
        takeLatest(fetchSectorsRequest.type, fetchSectorsSaga),
        takeLatest(fetchWaterAdministrationsRequest.type, fetchWaterAdministrationsSaga),
        takeLatest(fetchSubscriptionTypesRequest.type, fetchSubscriptionTypesSaga),
        takeLatest(fetchCompaniesRequest.type, fetchCompaniesSaga),
        takeLatest(fetchCompanyDetailRequest.type, fetchCompanyDetailSaga),
        takeLatest(updateCompanyRequest.type, updateCompanySaga),
        takeLatest(fetchAllCompaniesRequest.type, fetchAllCompaniesSaga),
        takeLatest(fetchCompanyTypesRequest.type, fetchCompanyTypesSaga),
        takeLatest(fetchCountriesRequest.type, fetchCountriesSaga),
        takeLatest(fetchDistrictsRequest.type, fetchDistrictsSaga),
        takeLatest(fetchCitiesRequest.type, fetchCitiesSaga),
        takeLatest(createCompanyRequest.type, createCompanySaga),
        takeEvery(startCopernicusQuery.type, handleCopernicusQuery),
        takeLatest(fetchProductFamiliesRequest.type, fetchProductFamiliesSaga),
        takeLatest(fetchUserRolesRequest.type, fetchUserRolesSaga),
        takeLatest(createUserRequest.type, createUserSaga),
        takeLatest(fetchUsersRequest.type, fetchAllUsersSaga),
        takeLatest(fetchUserDetailRequest.type, fetchUserDetailSaga),
        takeLatest(updateUserRequest.type, updateUserSaga),
        takeLatest(fetchGatewaysRequest.type, fetchGatewaysSaga),
        takeLatest(createGatewayRequest.type, createGatewaySaga),
        takeLatest(updateGatewayRequest.type, updateGatewaySaga),
        takeLatest(deleteGatewayRequest.type, deleteGatewaySaga),
        takeLatest(fetchSensorsRequest.type, fetchSensorsSaga),
        takeLatest(createSensorRequest.type, createSensorSaga),
        takeLatest(updateSensorRequest.type, updateSensorSaga),
        takeLatest(deleteSensorRequest.type, deleteSensorSaga),
        takeLatest(fetchBasinsRequest.type, fetchBasinsSaga),
        takeLatest(fetchWaterAdministrationMainSectorsRequest.type, fetchWaterAdministrationMainSectorsSaga),
        takeLatest(fetchWaterAdministrationSubSectorsRequest.type, fetchWaterAdministrationSubSectorsSaga)
    ]);
}